import { useEffect, useState } from "react";
import { Sparkles, Loader2, X, Lock, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const TONE_COLORS = {
  apologetic: "bg-[#E6F0EB] text-[#34745C]",
  investigative: "bg-[#FFF4E5] text-[#B07020]",
  empathetic: "bg-[#E8E1F5] text-[#5C4099]",
};

function toneClass(tone) {
  const t = (tone || "").toLowerCase();
  if (t.includes("apolog") || t.includes("action")) return TONE_COLORS.apologetic;
  if (t.includes("invest") || t.includes("measured")) return TONE_COLORS.investigative;
  return TONE_COLORS.empathetic;
}

export default function AISuggestedRepliesPanel({ familyId, enabled, onPickReply, onLockClick }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  async function generate() {
    if (!enabled) {
      if (onLockClick) onLockClick();
      else toast.info("This is part of the AI Messaging Assistant add-on ($49/mo).");
      return;
    }
    setOpen(true);
    setLoading(true);
    setData(null);
    try {
      const r = await api.post(`/messages/${familyId}/ai/suggest-replies`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not generate replies");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }

  function pick(text) {
    onPickReply(text);
    setOpen(false);
    toast.success("Reply inserted — review and send when ready.");
  }

  return (
    <>
      <button
        data-testid="ai-suggest-replies-btn"
        onClick={generate}
        type="button"
        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${enabled ? "bg-[#D6E0D9]/40 text-[#2E5C50] hover:bg-[#D6E0D9]/70" : "bg-[#F0EDE8] text-[#57605A] hover:bg-[#E8E6E1]"}`}
        title={enabled ? "Get 3 AI-drafted reply options" : "Requires AI Messaging Assistant add-on"}
      >
        {enabled ? <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />}
        Suggest replies
      </button>

      {open && (
        <div
          data-testid="ai-replies-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => !loading && setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl max-w-2xl w-full p-6 shadow-2xl max-h-[85vh] overflow-y-auto fade-in-up"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C07A60]">
                  ✨ AI Suggested replies
                </p>
                <h3 className="mt-1 font-heading font-semibold text-lg text-[#1C211F]">
                  Pick a tone — Nexari drafted 3 options
                </h3>
                {data?.based_on_messages != null && (
                  <p className="text-xs text-[#57605A] mt-0.5">Based on last {data.based_on_messages} messages.</p>
                )}
              </div>
              <button onClick={() => setOpen(false)} className="text-[#57605A] hover:text-[#1C211F]" aria-label="Close">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            {loading && (
              <div className="py-10 flex flex-col items-center gap-3">
                <Loader2 className="w-6 h-6 text-[#2E5C50] animate-spin" />
                <p className="text-sm text-[#57605A]">Reading the thread + drafting replies…</p>
              </div>
            )}

            {!loading && data?.note && (
              <p className="mt-5 text-sm text-[#57605A] py-6 text-center">{data.note}</p>
            )}

            {!loading && data?.replies?.length > 0 && (
              <div className="mt-5 space-y-3">
                {data.replies.map((r, i) => (
                  <div
                    key={i}
                    data-testid={`ai-reply-option-${i}`}
                    className="border border-[#E8E6E1] rounded-lg p-4 hover:border-[#2E5C50]/40 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${toneClass(r.tone)}`}>
                        {r.label || r.tone || `Option ${i + 1}`}
                      </span>
                      <div className="flex gap-2">
                        <button
                          data-testid={`ai-reply-copy-${i}`}
                          onClick={() => { navigator.clipboard.writeText(r.text); toast.success("Copied"); }}
                          className="text-xs text-[#57605A] hover:text-[#1C211F] inline-flex items-center gap-1"
                        >
                          <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy
                        </button>
                        <button
                          data-testid={`ai-reply-use-${i}`}
                          onClick={() => pick(r.text)}
                          className="text-xs bg-[#2E5C50] text-white hover:bg-[#22463D] px-3 py-1 rounded font-medium"
                        >
                          Use this
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-[#1C211F] whitespace-pre-wrap leading-relaxed">{r.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
